import {
  ArrowRightIcon,
  ArrowsLeftRightIcon,
  CaretDownIcon,
  CoinVerticalIcon,
  MoneyWavyIcon,
  SlidersHorizontalIcon,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { times } from "lodash-es";
import { useMemo, useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  FilterButton,
  FilterButtonSkeleton,
} from "@/components/FilterButton/FilterButton";
import { HeaderSearchButton } from "@/components/HeaderSearchButton/HeaderSearchButton";
import { StickyHeader } from "@/components/StickyHeader/StickyHeader";
import { FlightsListDrawerComponent } from "@/features/flights/components/flight-list/types";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import {
  TripType,
  type FlightFilters,
  type FlightsSortValue,
} from "@/features/flights/types/types";
import { SortOrder } from "@/types/enums";
import type { FlightFormLocation } from "@/types/types";
import { formatHeaderDate } from "@/utils/dates";

import { cabinClassLabels } from "../../utils/cabinClasses";
import { CalendarCards } from "../CalendarCards";

const sortOptionsLabels = defineMessages<SortOrder>({
  [SortOrder.ASC]: {
    defaultMessage: "Cheapest first",
    description: "Sort option: cheapest flights first",
  },
  [SortOrder.DESC]: {
    defaultMessage: "Expensive first",
    description: "Sort option: most expensive flights first",
  },
});

function formatLocation(location: FlightFormLocation) {
  return [
    location.code,
    "cityName" in location ? location.cityName : location.name,
  ]
    .filter(Boolean)
    .join(" ");
}

type Props = {
  selectedSortValue: FlightsSortValue;
  isLoading?: boolean;
  activeFilters: Record<keyof FlightFilters, boolean> | null;
  onFilterButtonClicked: (drawerComponent: FlightsListDrawerComponent) => void;
  onSearchDrawerOpen: () => void;
};

export function Header({
  selectedSortValue,
  isLoading,
  activeFilters,
  onSearchDrawerOpen,
  onFilterButtonClicked,
}: Props) {
  const intl = useIntl();
  const { searchData, updateSearchData } = useFlightSearchDataContext();

  const departureDate = searchData!.dates.departure;

  const passengerCount = useMemo(
    () => searchData!.adult + searchData!.child + searchData!.infantWithoutSeat,
    [searchData],
  );

  const handleDateChange = useCallback(
    (newDepartureDate: Date) => {
      updateSearchData((prevData) => {
        return {
          ...prevData!,
          dates: {
            ...prevData?.dates,
            departure: newDepartureDate,
          },
        };
      });
    },
    [updateSearchData],
  );

  return (
    <>
      <StickyHeader>
        <div className="flex items-center justify-between gap-2 py-4 text-text-primary">
          <div className="grow overflow-hidden">
            <HeaderSearchButton
              onClick={onSearchDrawerOpen}
              progress={isLoading ? { variant: "indeterminate" } : undefined}
            >
              <div className="flex w-full items-center gap-1.5 text-sm font-medium">
                <h3 className="truncate">{formatLocation(searchData!.from)}</h3>

                <span className="shrink-0">
                  {searchData!.tripType === TripType.ROUND_TRIP ? (
                    <ArrowsLeftRightIcon weight="bold" size={12} />
                  ) : (
                    <ArrowRightIcon
                      weight="bold"
                      size={12}
                      className="rtl:-scale-x-100"
                    />
                  )}
                </span>

                <h3 className="truncate">{formatLocation(searchData!.to)}</h3>
              </div>

              <div className="w-full truncate text-xs font-normal text-text-tertiary">
                {[
                  searchData!.tripType === TripType.ONE_WAY
                    ? formatHeaderDate(searchData!.dates.departure)
                    : `${formatHeaderDate(searchData!.dates.departure)} - ${formatHeaderDate(searchData!.dates.return!)}`,
                  intl.formatMessage(
                    {
                      defaultMessage:
                        "{count, plural, one {# traveller} other {# travellers}}",
                      description: "Total traveller count label",
                    },
                    { count: passengerCount },
                  ),
                  intl.formatMessage(cabinClassLabels[searchData!.cabinClass]),
                ].map((item, index) => (
                  <span key={index}>
                    {index > 0 ? <> &bull; </> : null}
                    {item}
                  </span>
                ))}
              </div>
            </HeaderSearchButton>
          </div>
        </div>
        <div
          className={clsx(
            "mb-2 flex gap-1 pt-px pb-2",
            isLoading ? "overflow-x-hidden" : "overflow-x-auto",
          )}
        >
          {isLoading ? (
            times(4, (idx) => <FilterButtonSkeleton key={idx} />)
          ) : (
            <>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.SORT)
                }
              >
                {selectedSortValue.order === SortOrder.ASC ? (
                  <CoinVerticalIcon size={20} />
                ) : (
                  <MoneyWavyIcon size={20} />
                )}
                {intl.formatMessage(sortOptionsLabels[selectedSortValue.order])}
              </FilterButton>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.ALL_FILTERS)
                }
                active={Object.values(activeFilters || {}).some(Boolean)}
              >
                <SlidersHorizontalIcon
                  size={20}
                  className="fill-text-primary"
                />
                {intl.formatMessage({
                  defaultMessage: "All filters",
                  description:
                    "Button to open the full filters and sort panel for flight results",
                })}
              </FilterButton>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.AIRLINES)
                }
                active={activeFilters?.selectedAirlines || false}
              >
                {intl.formatMessage({
                  defaultMessage: "Airlines",
                  description:
                    "Filter section label for filtering flights by airline",
                })}
                <CaretDownIcon size={16} className="fill-text-primary" />
              </FilterButton>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.STOPS)
                }
                active={activeFilters?.stops || false}
              >
                {intl.formatMessage({
                  defaultMessage: "Stops",
                  description: "Filter label for the stops / layovers filter",
                })}
                <CaretDownIcon size={16} className="fill-text-primary" />
              </FilterButton>
              <FilterButton
                onClick={() =>
                  onFilterButtonClicked(FlightsListDrawerComponent.PRICE_RANGE)
                }
                active={activeFilters?.priceRange || false}
              >
                {intl.formatMessage({
                  defaultMessage: "Price",
                  description:
                    "Filter section label for filtering flights by price",
                })}
                <CaretDownIcon size={16} className="fill-text-primary" />
              </FilterButton>
            </>
          )}
        </div>
      </StickyHeader>

      <div className="flex shrink-0 flex-col gap-4 bg-surface-background">
        {searchData!.tripType === TripType.ONE_WAY && (
          <CalendarCards
            departureDate={departureDate}
            onDateChange={handleDateChange}
          />
        )}
      </div>
    </>
  );
}
